// src/pages/cv/MyEmployeeCvPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import {
  certificateFileName,
  downloadCertificateFile,
  hasCertificateFile,
  openCertificateFile,
  uploadCertificateFile,
} from "./api/cvCertificateFiles";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  LinearProgress,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  InputAdornment,
  Tooltip,
  Fade,
  FormControlLabel,
  Zoom,
  Grow,
} from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";

import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import SchoolIcon from "@mui/icons-material/School";
import LanguageIcon from "@mui/icons-material/Language";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import TimelineIcon from "@mui/icons-material/Timeline";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PendingIcon from "@mui/icons-material/Pending";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CvProductTour from "../../components/onboarding/CvProductTour";

const safeMsg = (err, fallback = "Something went wrong") =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

const toDateInput = (v) => {
  if (!v) return "";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : "";
};

const uid = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

const TAB_TYPES = ["", "", "experience", "education", "language", "certificate"];

/* ----------------------- VALIDATION HELPERS ----------------------- */
const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";

const isValidEmail = (email) => {
  const s = String(email || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
};

const toNumber = (v) => {
  const n = Number(String(v ?? "").trim());
  return Number.isFinite(n) ? n : NaN;
};

/**
 * Submit rules (as requested):
 * - Experience: at least 1 item, required fields per item: company, position, location, start_date
 * - Education: at least 1 item, required fields per item: institution, degree, field, graduation_date
 * - Certificates: at least 1 item, required fields per item: title, issuer
 * - Languages: at least 1 item, required fields per item: language, proficiency
 */
const SECTION_SCHEMAS = {
  experience: [
    { key: "company", label: "Company / Organization" },
    { key: "position", label: "Position / Role" },
    { key: "location", label: "Location" },
    { key: "start_date", label: "Start Date" },
  ],
  education: [
    { key: "institution", label: "Institution" },
    { key: "degree", label: "Degree / Qualification" },
    { key: "field", label: "Field of Study" },
    { key: "graduation_date", label: "Graduation Date" },
  ],
  language: [
    { key: "language", label: "Language" },
    { key: "proficiency", label: "Proficiency" },
  ],
  certificate: [
    { key: "title", label: "Certificate Title" },
    { key: "issuer", label: "Issuing Organization" },
  ],
};

const validateRowBySchema = (type, row) => {
  const schema = SECTION_SCHEMAS[type] || [];
  const missing = [];
  schema.forEach((f) => {
    const v = row?.[f.key];
    if (isBlank(v)) missing.push(f.label);
  });
  return missing;
};

/* ----------------------- STYLED UI ----------------------- */
const PremiumCard = styled(Card)(({ theme }) => ({
  borderRadius: "14px",
  border: `1px solid ${alpha("#9db2c8", theme.palette.mode === "light" ? 0.34 : 0.28)}`,
  background:
    theme.palette.mode === "light"
      ? `linear-gradient(180deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#f7fbff", 0.94)} 100%)`
      : alpha(theme.palette.background.paper, 0.84),
  boxShadow: theme.palette.mode === "light" ? "0 16px 38px rgba(15, 23, 42, 0.07)" : "none",
  overflow: "hidden",
}));

const SectionTile = styled(Card, {
  shouldForwardProp: (prop) => prop !== "completed",
})(({ theme, completed }) => ({
  borderRadius: "12px",
  padding: theme.spacing(1.75),
  cursor: "pointer",
  border: `1px solid ${
    completed ? alpha(theme.palette.success.main, 0.35) : alpha("#9db2c8", 0.32)
  }`,
  background: completed
    ? alpha(theme.palette.success.main, 0.04)
    : alpha("#ffffff", theme.palette.mode === "light" ? 0.84 : 0.06),
  boxShadow: theme.palette.mode === "light" ? "0 10px 24px rgba(15, 23, 42, 0.05)" : "none",
  transition: "border-color 150ms ease, background 150ms ease",
  "&:hover": {
    borderColor: completed ? alpha(theme.palette.success.main, 0.42) : alpha(theme.palette.primary.main, 0.25),
    background: alpha(theme.palette.primary.main, 0.02),
  },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 48,
  "& .MuiTabs-indicator": { display: "none" },
  "& .MuiTab-root": {
    minHeight: 48,
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 850,
    padding: theme.spacing(0, 1.5),
    color: alpha("#102653", 0.76),
    transition: "all 180ms ease",
    "&.Mui-selected": {
      background: `linear-gradient(135deg, ${alpha("#2389ba", 0.2)} 0%, ${alpha("#1f3d7c", 0.12)} 100%)`,
      color: "#102653",
      boxShadow: `inset 0 0 0 1px ${alpha("#2389ba", 0.24)}`,
    },
  },
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 800,
  padding: theme.spacing(0.9, 1.7),
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 800,
  padding: theme.spacing(0.9, 1.7),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "status",
})(({ theme, status }) => {
  const map = {
    draft: {
      bg: alpha(theme.palette.warning.main, 0.12),
      fg: theme.palette.warning.dark,
      bd: alpha(theme.palette.warning.main, 0.25),
    },
    submitted: {
      bg: alpha(theme.palette.info.main, 0.12),
      fg: theme.palette.info.dark,
      bd: alpha(theme.palette.info.main, 0.25),
    },
    approved: {
      bg: alpha(theme.palette.success.main, 0.12),
      fg: theme.palette.success.dark,
      bd: alpha(theme.palette.success.main, 0.25),
    },
    rejected: {
      bg: alpha(theme.palette.error.main, 0.12),
      fg: theme.palette.error.dark,
      bd: alpha(theme.palette.error.main, 0.25),
    },
    locked: {
      bg: alpha(theme.palette.grey[700], 0.08),
      fg: theme.palette.grey[800],
      bd: theme.palette.divider,
    },
    closed: {
      bg: alpha(theme.palette.grey[700], 0.08),
      fg: theme.palette.grey[800],
      bd: theme.palette.divider,
    },
  };
  const c = map[status] || map.draft;

  return {
    borderRadius: "10px",
    height: 26,
    fontWeight: 800,
    fontSize: "0.75rem",
    backgroundColor: c.bg,
    color: c.fg,
    border: `1px solid ${c.bd}`,
    "& .MuiChip-icon": { color: c.fg },
  };
});

function StatCard({ title, value, icon, color }) {
  const theme = useTheme();
  const c = color || theme.palette.primary.main;

  return (
    <PremiumCard>
      <CardContent sx={{ p: 1.35, display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "14px",
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(c, 0.12),
            color: c,
            flex: "0 0 auto",
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 850 }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1.05, color: "#102653" }}>
            {value}
          </Typography>
        </Box>
      </CardContent>
    </PremiumCard>
  );
}

function getStatusInfo({ locked, canEdit, windowOpen, reviewStatus }) {
  if (reviewStatus === "approved") {
    return { label: "Approved", status: "approved", icon: <TaskAltIcon /> };
  }
  if (reviewStatus === "rejected") {
    return { label: "Rejected", status: "rejected", icon: <ErrorOutlineIcon /> };
  }
  if (locked) return { label: "Submitted & Locked", status: "locked", icon: <LockIcon /> };
  if (canEdit) return { label: "Draft - Editable", status: "draft", icon: <LockOpenIcon /> };
  return {
    label: windowOpen ? "Read-only" : "Window Closed",
    status: "closed",
    icon: <ErrorOutlineIcon />,
  };
}

export default function MyEmployeeCvPage() {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState({ type: "info", msg: "" });
  const [snack, setSnack] = useState({ open: false, msg: "" });
  const showSnack = (msg) => setSnack({ open: true, msg });

  const [cvMeta, setCvMeta] = useState({
    status: "draft",
    review_status: null,
    review_reason: null,
    reopen_reason: null,
    request_note: null,
    editable_until: null,
    submitted_at: null,
  });

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({
    full_name: "",
    email: "",
    phone: "",
    date_of_birth: "",
    current_job_title: "",
    years_experience: "",
    about_me: "",
    submitted_at: null,
  });

  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [certificates, setCertificates] = useState([]);

  // Validation state (blocks Submit + highlights fields)
  const [profileErrors, setProfileErrors] = useState({});
  const [dlgErrors, setDlgErrors] = useState({});

  const editableUntil = useMemo(
    () => (cvMeta?.editable_until ? new Date(cvMeta.editable_until) : null),
    [cvMeta?.editable_until]
  );

  const locked = useMemo(() => {
    return Boolean(
      profile?.submitted_at ||
        cvMeta?.submitted_at ||
        cvMeta?.status === "submitted" ||
        cvMeta?.status === "approved"
    );
  }, [profile?.submitted_at, cvMeta?.submitted_at, cvMeta?.status]);

  const windowOpen = useMemo(() => {
    if (!editableUntil) return true;
    return editableUntil.getTime() >= Date.now();
  }, [editableUntil]);

  const canEdit = useMemo(() => !locked && windowOpen, [locked, windowOpen]);

  const statusInfo = useMemo(
    () =>
      getStatusInfo({
        locked,
        canEdit,
        windowOpen,
        reviewStatus: cvMeta?.review_status || cvMeta?.status,
      }),
    [locked, canEdit, windowOpen, cvMeta?.review_status, cvMeta?.status]
  );

  const forceTour = useMemo(
    () => new URLSearchParams(location.search).get("tour") === "1",
    [location.search]
  );
  const [tourRestartSignal, setTourRestartSignal] = useState(0);

  const replayTour = useCallback(() => {
    setTab(0);
    setTourRestartSignal((prev) => prev + 1);
  }, []);

  const handleTourFinish = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tour") !== "1") return;

    params.delete("tour");
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ""}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const tabItems = useMemo(
    () => [
      { label: "Overview", icon: <AnalyticsIcon /> },
      { label: "Profile", icon: <PersonIcon /> },
      { label: "Experience", icon: <WorkIcon /> },
      { label: "Education", icon: <SchoolIcon /> },
      { label: "Languages", icon: <LanguageIcon /> },
      { label: "Certificates", icon: <EmojiEventsIcon /> },
    ],
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setInfo({ type: "info", msg: "" });
    setProfileErrors({});
    setDlgErrors({});

    try {
      const res = await api.get("/cv/me");
      const root = res?.data?.data ?? res?.data ?? {};
      const d = root?.data ?? root;

      setUser(d?.user ?? d?.me ?? null);

      const p = d?.profile || d?.cv_profile || d?.employee_cv_profile || {};
      const meta = d?.cv_meta || d?.meta || d?.cv || {};
      setCvMeta({
        status: meta?.status ?? (p?.submitted_at ? "submitted" : "draft"),
        review_status: meta?.review_status ?? p?.status ?? p?.review_status ?? null,
        review_reason: meta?.review_reason ?? p?.review_reason ?? null,
        reopen_reason: meta?.reopen_reason ?? p?.reopen_reason ?? null,
        request_note: meta?.request_note ?? null,
        editable_until: meta?.editable_until ?? p?.editable_until ?? null,
        submitted_at: meta?.submitted_at ?? p?.submitted_at ?? null,
      });

      setProfile({
        full_name: p.full_name || "",
        email: p.email || "",
        phone: p.phone || "",
        date_of_birth: toDateInput(p.date_of_birth),
        current_job_title: p.current_job_title || "",
        years_experience: p.years_experience ?? "",
        about_me: p.about_me || "",
        submitted_at: p.submitted_at || meta?.submitted_at || null,
      });

      const normalize = (arr) => (arr || []).map((x) => ({ _id: x._id || x.id || uid(), ...x }));

      setExperiences(normalize(p?.experiences || d?.experiences));
      setEducations(normalize(p?.educations || d?.educations));
      setLanguages(normalize(p?.languages || d?.languages));
      setCertificates(normalize(p?.certificates || d?.certificates));
    } catch (e) {
      setError(safeMsg(e, "Failed to load CV."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const completionPercentage = useMemo(() => {
    const profileOk =
      !!profile.full_name &&
      !!profile.email &&
      isValidEmail(profile.email) &&
      !!profile.phone &&
      !!profile.current_job_title &&
      String(profile.years_experience || "").trim().length > 0 &&
      !Number.isNaN(toNumber(profile.years_experience)) &&
      !!profile.about_me;

    const expOk =
      experiences.length > 0 &&
      experiences.every((r) => validateRowBySchema("experience", r).length === 0);

    const eduOk =
      educations.length > 0 &&
      educations.every((r) => validateRowBySchema("education", r).length === 0);

    const langOk =
      languages.length > 0 &&
      languages.every((r) => validateRowBySchema("language", r).length === 0);

    const certOk =
      certificates.length > 0 &&
      certificates.every((r) => validateRowBySchema("certificate", r).length === 0);

    const fields = [profileOk, expOk, eduOk, langOk, certOk];
    return Math.round((fields.filter(Boolean).length / fields.length) * 100);
  }, [profile, experiences, educations, languages, certificates]);

  const cvSummaryTiles = useMemo(
    () => [
      {
        label: "Years Experience",
        value: profile.years_experience || "0",
        unit: "Years",
        icon: <TimelineIcon />,
        color: theme.palette.info.main,
      },
      {
        label: "Work Experiences",
        value: experiences.length,
        unit: "Entries",
        icon: <WorkIcon />,
        color: theme.palette.success.main,
      },
      {
        label: "Education",
        value: educations.length,
        unit: "Entries",
        icon: <SchoolIcon />,
        color: theme.palette.warning.main,
      },
      {
        label: "Languages",
        value: languages.length,
        unit: "Languages",
        icon: <LanguageIcon />,
        color: theme.palette.info.dark,
      },
      {
        label: "Certificates",
        value: certificates.length,
        unit: "Certificates",
        icon: <EmojiEventsIcon />,
        color: theme.palette.secondary.main,
      },
    ],
    [profile.years_experience, experiences.length, educations.length, languages.length, certificates.length, theme]
  );

  const saveCore = useCallback(async () => {
    const payload = {
      ...profile,
      experiences: experiences.map(({ _id, id, _is_present, ...rest }) => rest),
      educations: educations.map(({ _id, id, ...rest }) => rest),
      languages: languages.map(({ _id, id, ...rest }) => rest),
      certificates: certificates.map(({ _id, id, _no_expiry, ...rest }) => rest),
    };
    delete payload.submitted_at;

    const res = await api.put("/cv/me", payload);
    const root = res?.data?.data ?? res?.data ?? {};
    const d = root?.data ?? root;

    const p = d?.profile || d?.cv_profile || d?.employee_cv_profile || null;
    const meta = d?.cv_meta || d?.meta || d?.cv || null;
    if (meta || p) {
        setCvMeta((prev) => ({
          ...prev,
          status: meta?.status ?? (p?.submitted_at ? "submitted" : prev.status),
          review_status: meta?.review_status ?? p?.review_status ?? prev.review_status,
          review_reason: meta?.review_reason ?? p?.review_reason ?? prev.review_reason,
          reopen_reason: meta?.reopen_reason ?? p?.reopen_reason ?? prev.reopen_reason,
          request_note: meta?.request_note ?? prev.request_note,
          editable_until: meta?.editable_until ?? p?.editable_until ?? prev.editable_until,
          submitted_at: meta?.submitted_at ?? p?.submitted_at ?? prev.submitted_at,
        }));
    }

    if (p) {
      setProfile((prev) => ({
        ...prev,
        full_name: p.full_name ?? prev.full_name,
        email: p.email ?? prev.email,
        phone: p.phone ?? prev.phone,
        date_of_birth: toDateInput(p.date_of_birth ?? prev.date_of_birth),
        current_job_title: p.current_job_title ?? prev.current_job_title,
        years_experience: p.years_experience ?? prev.years_experience,
        about_me: p.about_me ?? prev.about_me,
      }));
    }
  }, [profile, experiences, educations, languages, certificates]);

  const saveAll = useCallback(async () => {
    if (!canEdit) return showSnack(locked ? "CV is submitted and locked." : "Editing window is closed.");

    setBusy(true);
    setInfo({ type: "info", msg: "" });

    try {
      await saveCore();
      showSnack("Changes saved successfully");
    } catch (e) {
      setInfo({ type: "error", msg: safeMsg(e, "Failed to save changes.") });
    } finally {
      setBusy(false);
    }
  }, [canEdit, locked, saveCore]);

  // -------- CV auto-fill (read an uploaded CV via the local doc-AI engine) --------
  const applyCvToForm = useCallback((cv) => {
    // Parse the engine's free-text dates ("Mar 2021", "03/2021", "2021") -> ISO.
    const MONTHS = { jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06", jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12" };
    const parseDate = (v) => {
      const s = String(v || "").trim();
      if (!s || /present|current|now|ongoing/i.test(s)) return "";
      let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[1]}-${m[2]}-${m[3]}`;
      m = s.match(/([A-Za-z]{3,})\.?\s+(\d{4})/);
      if (m && MONTHS[m[1].slice(0, 3).toLowerCase()]) return `${m[2]}-${MONTHS[m[1].slice(0, 3).toLowerCase()]}-01`;
      m = s.match(/^(\d{1,2})[/.](\d{4})$/);
      if (m) return `${m[2]}-${m[1].padStart(2, "0")}-01`;
      m = s.match(/(?:^|\D)((?:19|20)\d{2})(?:\D|$)/);
      if (m) return `${m[1]}-01-01`;
      return "";
    };
    // "English (Fluent)" -> {language:"English", proficiency:"Fluent"}; else default.
    const splitLang = (s) => {
      const str = String(s || "").trim();
      const norm = (x) => {
        const t = String(x).toLowerCase();
        if (/native|mother/.test(t)) return "Native";
        if (/fluent|advanced|proficient|full/.test(t)) return "Fluent";
        if (/intermediate|good|working|profession/.test(t)) return "Intermediate";
        if (/basic|beginner|elementary|limited/.test(t)) return "Beginner";
        return "Intermediate";
      };
      const m = str.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
      if (m) return { language: m[1].trim() || str, proficiency: norm(m[2]) };
      return { language: str, proficiency: "Intermediate" };
    };

    setProfile((p) => ({
      ...p,
      full_name: cv.name || p.full_name,
      email: cv.email || p.email,
      phone: cv.phone || p.phone,
      current_job_title: cv.work_experience?.[0]?.title || p.current_job_title,
      years_experience:
        cv.total_years_experience != null ? String(cv.total_years_experience) : p.years_experience,
      about_me: cv.summary || p.about_me,
    }));

    // company + position are NOT NULL. Cross-fill if one is missing (the parser
    // sometimes puts the company in the title); drop the row if both are empty.
    const exps = (cv.work_experience || [])
      .map((w) => {
        const company = (w.company || "").trim();
        const position = (w.title || "").trim();
        const co = company || position;
        const po = position || company;
        if (!co) return null;
        return {
          _id: uid(),
          company: co,
          position: po,
          location: w.location || "",
          start_date: parseDate(w.start_date),
          end_date: w.is_current ? "" : parseDate(w.end_date),
          description: w.description || "",
          _is_present: !!w.is_current,
        };
      })
      .filter(Boolean);
    if (exps.length) setExperiences(exps);

    // institution + degree are NOT NULL - keep only complete rows.
    const edus = (cv.education || [])
      .filter((ed) => (ed.institution || "").trim() && (ed.degree || "").trim())
      .map((ed) => ({
        _id: uid(),
        institution: ed.institution.trim(),
        degree: ed.degree.trim(),
        field: ed.field || "",
        graduation_date: ed.year ? `${ed.year}-01-01` : "",
        description: "",
      }));
    if (edus.length) setEducations(edus);

    // proficiency is NOT NULL - parse "(level)" or default to Intermediate.
    const langs = (cv.languages || []).map((l) => ({ _id: uid(), ...splitLang(l) }));
    if (langs.length) setLanguages(langs);

    // title NOT NULL (from engine); issuer is nullable.
    const certs = (cv.certifications || []).map((c) => ({
      _id: uid(),
      title: String(c),
      issuer: "",
      issue_date: "",
      expiry_date: "",
      description: "",
      _no_expiry: false,
    }));
    if (certs.length) setCertificates(certs);
  }, []);

  const onScanFile = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      if (!canEdit) return showSnack(locked ? "CV is submitted and locked." : "Editing window is closed.");
      setBusy(true);
      setInfo({ type: "info", msg: "" });
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/document-ai/cv", fd);
        applyCvToForm(res?.data?.cv || {});
        showSnack("CV scanned — review the auto-filled fields before saving.");
      } catch (err) {
        setInfo({ type: "error", msg: safeMsg(err, "Failed to scan CV.") });
      } finally {
        setBusy(false);
      }
    },
    [canEdit, locked, applyCvToForm]
  );

  // -------- SUBMIT VALIDATION (MANDATORY BEFORE SUBMIT) --------
  const computeSubmitValidation = useCallback(() => {
    const pErrs = {};

    if (isBlank(profile.full_name)) pErrs.full_name = "Full Name is required.";
    if (isBlank(profile.email)) pErrs.email = "Email Address is required.";
    else if (!isValidEmail(profile.email)) pErrs.email = "Please enter a valid email address.";
    if (isBlank(profile.phone)) pErrs.phone = "Phone Number is required.";
    if (isBlank(profile.current_job_title)) pErrs.current_job_title = "Current / Proposed Position is required.";

    if (isBlank(profile.years_experience)) {
      pErrs.years_experience = "Years of Experience is required.";
    } else {
      const n = toNumber(profile.years_experience);
      if (Number.isNaN(n) || n < 0 || n > 50) {
        pErrs.years_experience = "Years of Experience must be a number between 0 and 50.";
      }
    }

    if (isBlank(profile.about_me)) pErrs.about_me = "About me is required.";

    // Required: at least one item in each section
    const missingSections = [];
    if (experiences.length < 1) missingSections.push("Experience (add at least 1 item)");
    if (educations.length < 1) missingSections.push("Education (add at least 1 item)");
    if (languages.length < 1) missingSections.push("Languages (add at least 1 item)");
    if (certificates.length < 1) missingSections.push("Certificates (add at least 1 item)");

    // Validate required fields per row (per SECTION_SCHEMAS)
    const rowIssues = {
      experience: [],
      education: [],
      language: [],
      certificate: [],
    };

    experiences.forEach((r) => {
      const missing = validateRowBySchema("experience", r);
      if (missing.length) rowIssues.experience.push({ id: r._id || r.id || uid(), missing });
    });

    educations.forEach((r) => {
      const missing = validateRowBySchema("education", r);
      if (missing.length) rowIssues.education.push({ id: r._id || r.id || uid(), missing });
    });

    languages.forEach((r) => {
      const missing = validateRowBySchema("language", r);
      if (missing.length) rowIssues.language.push({ id: r._id || r.id || uid(), missing });
    });

    certificates.forEach((r) => {
      const missing = validateRowBySchema("certificate", r);
      if (missing.length) rowIssues.certificate.push({ id: r._id || r.id || uid(), missing });
    });

    const hasRowIssues =
      rowIssues.experience.length ||
      rowIssues.education.length ||
      rowIssues.language.length ||
      rowIssues.certificate.length;

    const ok = Object.keys(pErrs).length === 0 && missingSections.length === 0 && !hasRowIssues;

    const firstTab = (() => {
      if (Object.keys(pErrs).length) return 1;

      const missingExp = experiences.length < 1;
      const missingEdu = educations.length < 1;
      const missingLang = languages.length < 1;
      const missingCert = certificates.length < 1;

      if (missingExp || rowIssues.experience.length) return 2;
      if (missingEdu || rowIssues.education.length) return 3;
      if (missingLang || rowIssues.language.length) return 4;
      if (missingCert || rowIssues.certificate.length) return 5;

      return 0;
    })();

    return { ok, pErrs, missingSections, rowIssues, firstTab };
  }, [profile, experiences, educations, languages, certificates]);

  const runSubmitValidation = useCallback(() => {
    const v = computeSubmitValidation();
    setProfileErrors(v.pErrs);

    if (!v.ok) {
      const parts = [];

      const profileMissing = Object.values(v.pErrs);
      if (profileMissing.length) parts.push(`Profile: ${profileMissing.join(" ")}`);

      if (v.missingSections.length) parts.push(`Sections: ${v.missingSections.join(" / ")}`);

      const rowCounts = [];
      if (v.rowIssues.experience.length)
        rowCounts.push(`Experience: ${v.rowIssues.experience.length} incomplete item(s)`);
      if (v.rowIssues.education.length)
        rowCounts.push(`Education: ${v.rowIssues.education.length} incomplete item(s)`);
      if (v.rowIssues.language.length)
        rowCounts.push(`Languages: ${v.rowIssues.language.length} incomplete item(s)`);
      if (v.rowIssues.certificate.length)
        rowCounts.push(`Certificates: ${v.rowIssues.certificate.length} incomplete item(s)`);

      if (rowCounts.length) parts.push(rowCounts.join(" / "));

      setInfo({
        type: "error",
        msg: `Please complete all required fields before submitting. ${parts.join(" | ")}`.trim(),
      });

      setTab(v.firstTab);
    } else {
      setInfo({ type: "info", msg: "" });
    }

    return v.ok;
  }, [computeSubmitValidation]);

  const submitAll = useCallback(async () => {
    if (!canEdit) return showSnack(locked ? "Already submitted." : "Editing window is closed.");

    const valid = runSubmitValidation();
    if (!valid) return;

    const ok = window.confirm("Submit your CV? This will lock editing for HR review.");
    if (!ok) return;

    setBusy(true);
    setInfo({ type: "info", msg: "" });

    try {
      await saveCore();
      const res = await api.post("/cv/me/submit");
      const root = res?.data?.data ?? res?.data ?? {};
      const d = root?.data ?? root;

      const submitted_at = d?.submitted_at || d?.cv_meta?.submitted_at || new Date().toISOString();
      setProfile((prev) => ({ ...prev, submitted_at }));
      setCvMeta((prev) => ({ ...prev, status: "submitted", submitted_at }));
      showSnack("CV submitted successfully!");
    } catch (e) {
      setInfo({ type: "error", msg: safeMsg(e, "Failed to submit CV.") });
    } finally {
      setBusy(false);
    }
  }, [canEdit, locked, runSubmitValidation, saveCore]);

  // Dialog state
  const [dlg, setDlg] = useState({ open: false, type: "", mode: "add", row: null });
  const [form, setForm] = useState({});

  const openDialog = useCallback(
    (type, mode, row = null) => {
      if (!canEdit) return showSnack(locked ? "CV is submitted and locked." : "Editing window is closed.");

      setDlg({ open: true, type, mode, row });
      setDlgErrors({});

      const baseForm = row ? { ...row } : {};
      ["start_date", "end_date", "graduation_date", "issue_date", "expiry_date"].forEach((f) => {
        if (baseForm[f]) baseForm[f] = toDateInput(baseForm[f]);
      });
      if (type === "experience") {
        baseForm._is_present = row ? !baseForm.end_date : false;
      }
      if (type === "certificate") {
        baseForm._no_expiry = row ? !baseForm.expiry_date : false;
      }
      setForm(baseForm);
    },
    [canEdit, locked]
  );

  const closeDialog = useCallback(() => {
    setDlg({ open: false, type: "", mode: "add", row: null });
    setForm({});
    setDlgErrors({});
  }, []);

  const saveDialogLocal = useCallback(() => {
    if (!canEdit) return showSnack(locked ? "CV is submitted and locked." : "Editing window is closed.");

    const normalizedForm = { ...form };
    if (dlg.type === "experience" && normalizedForm._is_present) {
      normalizedForm.end_date = "";
    }
    if (dlg.type === "certificate" && normalizedForm._no_expiry) {
      normalizedForm.expiry_date = "";
    }

    // Enforce ONLY required fields for this item
    const schema = SECTION_SCHEMAS[dlg.type] || [];
    const errs = {};
    schema.forEach((f) => {
      if (isBlank(normalizedForm?.[f.key])) errs[f.key] = `${f.label} is required.`;
    });

    setDlgErrors(errs);
    if (Object.keys(errs).length) {
      setInfo({ type: "error", msg: "Please fill required fields before saving this item." });
      return;
    }

    const rowKey = dlg.row?._id || dlg.row?.id || uid();
    const newRow = { _id: rowKey, ...normalizedForm };

    const setters = {
      experience: setExperiences,
      education: setEducations,
      language: setLanguages,
      certificate: setCertificates,
    };

    const setter = setters[dlg.type];
    if (setter) {
      setter((prev) =>
        dlg.mode === "add"
          ? [newRow, ...prev]
          : prev.map((x) => ((x._id || x.id) === rowKey ? newRow : x))
      );
    }

    setInfo({ type: "info", msg: "" });
    closeDialog();
  }, [canEdit, locked, dlg.type, dlg.mode, dlg.row, form, closeDialog]);

  const removeRow = useCallback(
    (type, row) => {
      if (!canEdit) return showSnack(locked ? "CV is submitted and locked." : "Editing window is closed.");
      const ok = window.confirm("Are you sure you want to delete this item?");
      if (!ok) return;

      const key = row?._id || row?.id;

      const setters = {
        experience: setExperiences,
        education: setEducations,
        language: setLanguages,
        certificate: setCertificates,
      };

      const setter = setters[type];
      if (setter) {
        setter((prev) => prev.filter((x) => (x._id || x.id) !== key));
      }
    },
    [canEdit, locked]
  );

  /* ------------------- certificate attachments ------------------- */
  const [certFileBusy, setCertFileBusy] = useState("");

  const runCertificateFile = useCallback(async (cert, mode) => {
    setCertFileBusy(`${cert?.id || ""}:${mode}`);
    try {
      if (mode === "download") await downloadCertificateFile(cert);
      else await openCertificateFile(cert);
    } catch (e) {
      showSnack(e?.message || "Could not open the attachment.");
    } finally {
      setCertFileBusy("");
    }
  }, []);

  const uploadDialogCertificateFile = useCallback(
    async (file) => {
      if (!file) return;
      setCertFileBusy("dialog:upload");
      try {
        const out = await uploadCertificateFile(file);
        if (out?.file_path) {
          setForm((p) => ({ ...p, file_path: out.file_path, file_name: out.file_name || "" }));
          showSnack("Attachment uploaded. Save the CV to keep it.");
        }
      } catch (e) {
        showSnack(safeMsg(e, "Upload failed."));
      } finally {
        setCertFileBusy("");
      }
    },
    []
  );
  const listType = useMemo(() => TAB_TYPES[tab] || "", [tab]);

  const listData = useMemo(() => {
    if (tab === 2) return experiences;
    if (tab === 3) return educations;
    if (tab === 4) return languages;
    if (tab === 5) return certificates;
    return [];
  }, [tab, experiences, educations, languages, certificates]);

  const listColumns = useMemo(() => {
    const colsByType = {
      experience: [
        { field: "company", headerName: "Company", flex: 1.2, minWidth: 220 },
        { field: "position", headerName: "Position", flex: 1, minWidth: 180 },
        { field: "start_date", headerName: "Start Date", width: 130 },
        {
          field: "end_date",
          headerName: "End Date",
          width: 130,
          renderCell: (params) => params?.row?.end_date || "Present",
        },
      ],
      education: [
        { field: "institution", headerName: "Institution", flex: 1.2, minWidth: 240 },
        { field: "degree", headerName: "Degree", flex: 1, minWidth: 180 },
        { field: "field", headerName: "Field", flex: 1, minWidth: 180 },
        { field: "graduation_date", headerName: "Graduation", width: 130 },
      ],
      language: [
        { field: "language", headerName: "Language", flex: 1, minWidth: 200 },
        { field: "proficiency", headerName: "Proficiency", flex: 1, minWidth: 220 },
      ],
      certificate: [
        { field: "title", headerName: "Title", flex: 1.2, minWidth: 280 },
        { field: "issuer", headerName: "Issuer", flex: 1, minWidth: 220 },
        { field: "issue_date", headerName: "Issue Date", width: 130 },
        {
          field: "expiry_date",
          headerName: "Expiry Date",
          width: 130,
          renderCell: (params) => params?.row?.expiry_date || "No Expiry",
        },
        {
          field: "file_path",
          headerName: "Attachment",
          width: 180,
          sortable: false,
          filterable: false,
          renderCell: (params) => {
            const row = params?.row || {};
            if (!hasCertificateFile(row)) {
              return (
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                  None
                </Typography>
              );
            }
            const saved = Boolean(row?.id);
            return (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title={certificateFileName(row) || "Attachment"}>
                  <AttachFileIcon fontSize="small" color="action" />
                </Tooltip>
                <Tooltip title={saved ? "Open" : "Save the CV to open this file"}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!saved || certFileBusy === `${row.id}:open`}
                      onClick={() => runCertificateFile(row, "open")}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={saved ? "Download" : "Save the CV to download this file"}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!saved || certFileBusy === `${row.id}:download`}
                      onClick={() => runCertificateFile(row, "download")}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            );
          },
        },
      ],
    };

    const cols = [...(colsByType[listType] || [])];

    cols.push({
      field: "actions",
      headerName: "",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit">
            <span>
              <IconButton
                size="small"
                onClick={() => openDialog(listType, "edit", params.row)}
                disabled={!canEdit}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: "primary.main",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <span>
              <IconButton
                size="small"
                onClick={() => removeRow(listType, params.row)}
                disabled={!canEdit}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                    color: "error.main",
                  },
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    });

    return cols;
  }, [listType, canEdit, theme, openDialog, removeRow, certFileBusy, runCertificateFile]);

  const dialogFields = useMemo(() => {
    // Required flags: ONLY main required fields as requested
    const configs = {
      experience: [
        { key: "company", label: "Company / Organization", required: true },
        { key: "position", label: "Position / Role", required: true },
        { key: "location", label: "Location", required: true },
        { key: "start_date", label: "Start Date", type: "date", required: true },
        { key: "end_date", label: "End Date", type: "date", required: false },
        { key: "description", label: "Notes", multiline: true, rows: 4, required: false },
      ],
      education: [
        { key: "institution", label: "Institution", required: true },
        { key: "degree", label: "Degree / Qualification", required: true },
        { key: "field", label: "Field of Study", required: true },
        { key: "graduation_date", label: "Graduation Date", type: "date", required: true },
        { key: "description", label: "Notes", multiline: true, rows: 3, required: false },
      ],
      language: [
        { key: "language", label: "Language", required: true },
        { key: "proficiency", label: "Proficiency", placeholder: "Native / Fluent / Intermediate / Basic", required: true },
      ],
      certificate: [
        { key: "title", label: "Certificate Title", required: true },
        { key: "issuer", label: "Issuing Organization", required: true },
        { key: "issue_date", label: "Issue Date", type: "date", required: false },
        { key: "expiry_date", label: "Expiry Date", type: "date", required: false },
        { key: "description", label: "Details", multiline: true, rows: 3, required: false },
      ],
    };
    return configs[dlg.type] || [];
  }, [dlg.type]);

  // --- UI states ---
  const pageBackground =
    theme.palette.mode === "light"
      ? `radial-gradient(900px 320px at 14% 0%, ${alpha("#1aa4c8", 0.12)}, transparent 58%), linear-gradient(180deg, #f6fbff 0%, #edf3f9 100%)`
      : theme.hr?.gradient?.page ||
        `radial-gradient(900px 300px at 0% 0%, ${alpha(theme.palette.primary.main, 0.08)}, transparent 58%), ${theme.palette.background.default}`;

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "background.default", background: pageBackground }}>
        <Stack alignItems="center" spacing={2.5}>
          <CircularProgress size={56} thickness={4} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: "text.secondary" }}>
            Loading...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <PremiumCard>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, mb: 2 }}>
              Unable to Load Profile
            </Typography>
            <Alert severity="error" sx={{ borderRadius: "12px", mb: 2 }}>
              <Typography sx={{ fontWeight: 800 }}>{error}</Typography>
            </Alert>
            <PrimaryButton startIcon={<RefreshIcon />} onClick={load}>
              Refresh
            </PrimaryButton>
          </CardContent>
        </PremiumCard>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", background: pageBackground, py: { xs: 1.25, md: 1.75 } }}>
      <CvProductTour
        isReady={!loading}
        canEdit={canEdit}
        locked={locked}
        forceStart={forceTour}
        restartSignal={tourRestartSignal}
        setTab={setTab}
        onFinish={handleTourFinish}
      />
      <Container maxWidth="xl">
        {/* HERO */}
        <PremiumCard
          data-tour="cv-hero"
          sx={{
            mb: 2,
            overflow: "hidden",
            background:
              theme.palette.mode === "light"
                ? `linear-gradient(135deg, ${alpha("#ffffff", 0.98)} 0%, ${alpha("#f6fbff", 0.96)} 48%, ${alpha("#edf7fd", 0.94)} 100%)`
                : theme.hr?.gradient?.hero || `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
            color: theme.palette.mode === "light" ? "#102653" : "white",
            position: "relative",
            borderColor: alpha("#9db2c8", 0.36),
            boxShadow: theme.palette.mode === "light" ? "0 18px 46px rgba(15, 23, 42, 0.09)" : undefined,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: theme.palette.mode === "light" ? 0.58 : 0.08,
              pointerEvents: "none",
              display: { xs: "none", md: "block" },
            }}
          >
            <svg viewBox="0 0 1160 260" width="100%" height="100%" preserveAspectRatio="none">
              <path
                d="M48 150 C150 201, 253 166, 355 111 C449 59, 528 51, 620 96 C714 142, 768 181, 861 108 C948 39, 1014 84, 1076 137 C1112 166, 1135 151, 1150 128"
                fill="none"
                stroke="#1aa4c8"
                strokeWidth="3"
                strokeDasharray="10 14"
                strokeLinecap="round"
              />
              <path d="M430 178 L632 62 L840 168 Z" fill="none" stroke="#d8e8f3" strokeWidth="1" />
              <circle cx="630" cy="98" r="8" fill="#0d75d9" opacity="0.82" />
              <circle cx="862" cy="108" r="8" fill="#22a35a" opacity="0.82" />
              <circle cx="1002" cy="98" r="8" fill="#f0a329" opacity="0.82" />
            </svg>
          </Box>

          <CardContent sx={{ p: { xs: 1.75, md: 2.25 }, position: "relative" }}>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                alignItems: "center",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(460px, 0.92fr)" },
              }}
            >
              {/* Left */}
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      background: "linear-gradient(135deg, #0f9cc2 0%, #214a9a 100%)",
                      color: "white",
                      fontWeight: 900,
                      fontSize: "1.35rem",
                      border: `3px solid ${alpha("#ffffff", 0.9)}`,
                      boxShadow: "0 14px 28px rgba(15, 117, 217, 0.2)",
                    }}
                  >
                    {(profile.full_name || "CV").charAt(0).toUpperCase()}
                  </Avatar>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 900,
                        letterSpacing: 0,
                        lineHeight: 1.1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: theme.palette.mode === "light" ? "#102653" : "white",
                      }}
                    >
                      My CV Workspace
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: theme.palette.mode === "light" ? alpha("#102653", 0.72) : alpha("#ffffff", 0.9),
                        fontWeight: 750,
                        mt: 0.5,
                        overflow: "hidden",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {profile.full_name || "My Professional Profile"} - {profile.current_job_title || "Update your position and information"}
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 1.5 }}
                  data-tour="cv-status-chips"
                >
                  <StatusChip label={statusInfo.label} status={statusInfo.status} icon={statusInfo.icon} />

                  {user?.employee_code && (
                    <StatusChip
                      label={`Employee ID: ${user.employee_code}`}
                      status="draft"
                      icon={<DescriptionIcon />}
                    />
                  )}

                  {profile.submitted_at && (
                    <StatusChip
                      label={`Submitted: ${new Date(profile.submitted_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}`}
                      status="submitted"
                      icon={<CheckCircleOutlineIcon />}
                    />
                  )}

                  {!locked && cvMeta?.editable_until && (
                    <StatusChip
                      label={`Editable until: ${new Date(cvMeta.editable_until).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}`}
                      status={windowOpen ? "draft" : "closed"}
                      icon={<PendingIcon />}
                    />
                  )}
                </Stack>

                {cvMeta?.review_status === "rejected" && (
                  <Alert severity="error" sx={{ mt: 1.25, borderRadius: "12px" }}>
                    <Typography sx={{ fontWeight: 800, mb: 0.5 }}>CV Rejected</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {cvMeta?.review_reason || "Please check your HR note for details."}
                    </Typography>
                  </Alert>
                )}

                {cvMeta?.review_status === "approved" && (
                  <Alert severity="success" sx={{ mt: 1.25, borderRadius: "12px" }}>
                    <Typography sx={{ fontWeight: 800, mb: 0.5 }}>CV Approved</Typography>
                    {cvMeta?.review_reason && (
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {cvMeta.review_reason}
                      </Typography>
                    )}
                  </Alert>
                )}

                {(cvMeta?.reopen_reason || cvMeta?.request_note) && (
                  <Alert severity="info" sx={{ mt: 1.25, borderRadius: "12px" }}>
                    <Typography sx={{ fontWeight: 800, mb: 0.5 }}>HR Note</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {cvMeta.request_note || cvMeta.reopen_reason}
                    </Typography>
                  </Alert>
                )}
              </Box>

              {/* Right */}
              <Box>
                <Stack spacing={1.5} alignItems={{ xs: "stretch", md: "flex-end" }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                    <Tooltip title="Replay CV guide">
                      <SecondaryButton
                        data-tour="cv-help-replay"
                        onClick={replayTour}
                        disabled={busy}
                        sx={{
                          minWidth: 44,
                          px: 1.2,
                          bgcolor: alpha("#ffffff", 0.86),
                          color: "#102653",
                          borderColor: alpha("#1f3d7c", 0.24),
                          "&:hover": {
                            bgcolor: "#ffffff",
                            borderColor: alpha("#1f3d7c", 0.35),
                          },
                        }}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </SecondaryButton>
                    </Tooltip>

                    <SecondaryButton
                      startIcon={<RefreshIcon />}
                      onClick={load}
                      disabled={busy}
                      sx={{
                        bgcolor: alpha("#ffffff", 0.86),
                        color: "#102653",
                        borderColor: alpha("#1f3d7c", 0.24),
                        "&:hover": {
                          bgcolor: "#ffffff",
                          borderColor: alpha("#1f3d7c", 0.35),
                        },
                      }}
                    >
                      Refresh
                    </SecondaryButton>

                    {!locked && (
                      <>
                        <SecondaryButton
                          data-tour="cv-save-draft"
                          startIcon={<SaveIcon />}
                          onClick={saveAll}
                          disabled={busy || !canEdit}
                          sx={{
                            bgcolor: alpha("#ffffff", 0.86),
                            color: "#102653",
                            borderColor: alpha("#1f3d7c", 0.24),
                            "&:hover": {
                              bgcolor: "#ffffff",
                              borderColor: alpha("#1f3d7c", 0.35),
                            },
                          }}
                        >
                          Save Draft
                        </SecondaryButton>

                        <SecondaryButton
                          component="label"
                          disabled={busy || !canEdit}
                          sx={{
                            bgcolor: alpha("#ffffff", 0.86),
                            color: "#102653",
                            borderColor: alpha("#1f3d7c", 0.24),
                            "&:hover": {
                              bgcolor: "#ffffff",
                              borderColor: alpha("#1f3d7c", 0.35),
                            },
                          }}
                        >
                          Scan CV → Auto-fill
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.doc,.docx,.txt,image/*"
                            onChange={onScanFile}
                          />
                        </SecondaryButton>

                        <PrimaryButton
                          data-tour="cv-submit-cv"
                          startIcon={<CheckCircleIcon />}
                          onClick={submitAll}
                          disabled={busy || !canEdit}
                          sx={{
                            background: "linear-gradient(135deg, #214a9a 0%, #0f9cc2 100%)",
                            color: "white",
                            boxShadow: "0 12px 24px rgba(15, 117, 217, 0.22)",
                            "&:hover": { boxShadow: "0 14px 28px rgba(15, 117, 217, 0.28)" },
                          }}
                        >
                          Submit CV
                        </PrimaryButton>
                      </>
                    )}
                  </Stack>

                  <Typography
                    data-tour="cv-editing-note"
                    variant="caption"
                    sx={{
                      color: theme.palette.mode === "light" ? alpha("#102653", 0.64) : alpha("#ffffff", 0.85),
                      textAlign: { xs: "left", md: "right" },
                      fontStyle: "italic",
                    }}
                  >
                    {cvMeta?.review_status === "rejected"
                      ? "CV rejected. Please update your CV and resubmit."
                      : cvMeta?.review_status === "approved"
                      ? "CV approved. No action needed unless you want to update your info."
                      : locked
                      ? "Submitted for HR review. Contact HR for changes."
                      : canEdit
                      ? "Save anytime. Submit when ready for review."
                      : "Editing window closed. Contact HR to reopen."}
                  </Typography>
                </Stack>
              </Box>
            </Box>

            {/* Progress */}
            <Box sx={{ mt: 1.75 }} data-tour="cv-completion">
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 900, color: theme.palette.mode === "light" ? "#102653" : "white" }}>
                  Completion
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 900, color: theme.palette.mode === "light" ? "#0f94bf" : "white" }}>
                  {completionPercentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completionPercentage}
                sx={{
                  height: 7,
                  borderRadius: 99,
                  bgcolor: alpha("#9db2c8", 0.28),
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 99,
                    background: "linear-gradient(90deg, #214a9a 0%, #0f9cc2 100%)",
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                mt: 1.6,
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  lg: "repeat(5, minmax(0, 1fr))",
                },
                gap: 0.9,
              }}
            >
              {cvSummaryTiles.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    p: 1.15,
                    minHeight: 92,
                    borderRadius: "12px",
                    border: `1px solid ${alpha(item.color, 0.18)}`,
                    backgroundColor: alpha(item.color, 0.065),
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "14px",
                      color: item.color,
                      backgroundColor: alpha(item.color, 0.12),
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 850 }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ color: "#102653", fontWeight: 950, fontSize: "1.55rem", lineHeight: 1 }}>
                      {item.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: alpha("#102653", 0.62), fontWeight: 700 }}>
                      {item.unit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </PremiumCard>

        {!!info?.msg && (
          <Grow in={!!info.msg}>
            <Alert
              severity={info.type === "error" ? "error" : "info"}
              sx={{ mb: 1.5, borderRadius: "12px" }}
              onClose={() => setInfo({ type: "info", msg: "" })}
            >
              <Typography sx={{ fontWeight: 800 }}>{info.msg}</Typography>
            </Alert>
          </Grow>
        )}

        {/* MAIN LAYOUT */}
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", md: "300px minmax(0, 1fr)" },
            alignItems: "start",
          }}
        >
          {/* Sidebar */}
          <Stack spacing={1.25}>
            <StatCard
              title="Years of Experience"
              value={profile.years_experience || "0"}
              icon={<TimelineIcon />}
              color={theme.palette.info.main}
            />
            <StatCard
              title="Work Experiences"
              value={experiences.length}
              icon={<WorkIcon />}
              color={theme.palette.success.main}
            />
            <StatCard title="Education" value={educations.length} icon={<SchoolIcon />} color={theme.palette.warning.main} />
            <StatCard
              title="Languages"
              value={languages.length}
              icon={<LanguageIcon />}
              color={theme.palette.secondary.main}
            />
            <StatCard
              title="Certificates"
              value={certificates.length}
              icon={<EmojiEventsIcon />}
              color={theme.palette.error.main}
            />

            <PremiumCard data-tour="cv-tips-card">
              <CardContent sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                  <InfoOutlinedIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>
                    Tips
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  {[
                    "Complete all sections for better visibility",
                    "Experience requires Company + Position",
                    "Education requires Institution",
                    "Languages require Language + Proficiency",
                    "Certificates require Title + Issuer",
                  ].map((t, i) => (
                    <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <TaskAltIcon sx={{ fontSize: 16, color: "success.main", mt: 0.25 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        {t}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </PremiumCard>
          </Stack>

          {/* Content */}
          <PremiumCard>
            <Box sx={{ px: { xs: 1.25, md: 1.75 }, borderBottom: "1px solid", borderColor: "divider" }}>
              <StyledTabs
                data-tour="cv-tabs"
                value={tab}
                onChange={(e, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                {tabItems.map((item, i) => (
                  <Tab
                    key={i}
                    label={item.label}
                    icon={item.icon}
                    iconPosition="start"
                    data-tour={i === 1 ? "cv-tab-profile" : undefined}
                  />
                ))}
              </StyledTabs>
            </Box>

            <Box sx={{ p: { xs: 1.5, md: 1.75 } }}>
              {/* Overview */}
              {tab === 0 && (
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: "-0.3px" }}>
                      Complete Your Profile
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mt: 0.5 }}>
                      Fill each required section to submit your CV for HR review.
                    </Typography>
                  </Box>

                  <Box
                    data-tour="cv-overview-grid"
                    sx={{
                      display: "grid",
                      gap: 1.25,
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                    }}
                  >
                    {[
                      {
                        label: "Personal Profile",
                        icon: <PersonIcon />,
                        completed:
                          !!profile.full_name &&
                          !!profile.email &&
                          isValidEmail(profile.email) &&
                          !!profile.phone &&
                          !!profile.current_job_title &&
                          String(profile.years_experience || "").trim().length > 0 &&
                          !Number.isNaN(toNumber(profile.years_experience)) &&
                          !!profile.about_me,
                        toTab: 1,
                        description: "Contact info + summary",
                      },
                      {
                        label: "Work Experience",
                        icon: <WorkIcon />,
                        completed:
                          experiences.length > 0 &&
                          experiences.every((r) => validateRowBySchema("experience", r).length === 0),
                        toTab: 2,
                        description: "Company + Position + Location + Start Date required",
                      },
                      {
                        label: "Education",
                        icon: <SchoolIcon />,
                        completed:
                          educations.length > 0 &&
                          educations.every((r) => validateRowBySchema("education", r).length === 0),
                        toTab: 3,
                        description: "All fields required except note",
                      },
                      {
                        label: "Languages",
                        icon: <LanguageIcon />,
                        completed:
                          languages.length > 0 &&
                          languages.every((r) => validateRowBySchema("language", r).length === 0),
                        toTab: 4,
                        description: "Language + Proficiency required",
                      },
                      {
                        label: "Certifications",
                        icon: <EmojiEventsIcon />,
                        completed:
                          certificates.length > 0 &&
                          certificates.every((r) => validateRowBySchema("certificate", r).length === 0),
                        toTab: 5,
                        description: "Title + Issuer required",
                      },
                    ].map((s, idx) => (
                      <SectionTile key={idx} completed={s.completed} onClick={() => setTab(s.toTab)}>
                        <Stack spacing={1.25}>
                          <Stack direction="row" spacing={1.25} alignItems="flex-start" justifyContent="space-between">
                            <Stack direction="row" spacing={1.1} alignItems="center">
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: "10px",
                                  display: "grid",
                                  placeItems: "center",
                                  bgcolor: s.completed
                                    ? alpha(theme.palette.success.main, 0.14)
                                    : alpha(theme.palette.grey[500], 0.12),
                                  color: s.completed ? "success.main" : "text.secondary",
                                }}
                              >
                                {s.icon}
                              </Box>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                  {s.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                  {s.description}
                                </Typography>
                              </Box>
                            </Stack>

                            <Chip
                              size="small"
                              variant="outlined"
                              label={s.completed ? "Complete" : "In progress"}
                              color={s.completed ? "success" : "default"}
                              sx={{ fontWeight: 900 }}
                            />
                          </Stack>

                          <Button
                            variant="text"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                              alignSelf: "flex-start",
                              fontWeight: 900,
                              textTransform: "none",
                              px: 0,
                              "&:hover": { bgcolor: "transparent", opacity: 0.9 },
                            }}
                          >
                            {s.completed ? "Review" : "Edit"}
                          </Button>
                        </Stack>
                      </SectionTile>
                    ))}
                  </Box>
                </Stack>
              )}

              {/* Profile */}
              {tab === 1 && (
                <Stack spacing={1.75}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900 }}>
                      Personal Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mt: 0.5 }}>
                      Update your contact details and summary.
                    </Typography>
                  </Box>

                  {!canEdit && (
                    <Alert severity="info" sx={{ borderRadius: "12px" }}>
                      <Typography sx={{ fontWeight: 800 }}>
                        Profile is {locked ? "locked for HR review" : "read-only"}. Contact HR for changes.
                      </Typography>
                    </Alert>
                  )}

                  <Stack spacing={1.75}>
                    <Box data-tour="cv-contact-section">
                      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1.5 }}>
                        Contact
                      </Typography>

                      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" } }}>
                        <TextField
                          label="Full Name"
                          value={profile.full_name}
                          onChange={(e) => {
                            const val = e.target.value;
                            setProfile((p) => ({ ...p, full_name: val }));
                            if (profileErrors.full_name) {
                              setProfileErrors((prev) => {
                                const { full_name, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                          fullWidth
                          disabled={!canEdit}
                          required
                          error={!!profileErrors.full_name}
                          helperText={profileErrors.full_name || " "}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon sx={{ color: "text.secondary" }} />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <TextField
                          label="Email Address"
                          value={profile.email}
                          onChange={(e) => {
                            const val = e.target.value;
                            setProfile((p) => ({ ...p, email: val }));
                            if (profileErrors.email) {
                              setProfileErrors((prev) => {
                                const { email, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                          fullWidth
                          disabled={!canEdit}
                          required
                          error={!!profileErrors.email}
                          helperText={profileErrors.email || " "}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon sx={{ color: "text.secondary" }} />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <TextField
                          label="Phone Number"
                          value={profile.phone}
                          onChange={(e) => {
                            const val = e.target.value;
                            setProfile((p) => ({ ...p, phone: val }));
                            if (profileErrors.phone) {
                              setProfileErrors((prev) => {
                                const { phone, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                          fullWidth
                          disabled={!canEdit}
                          required
                          error={!!profileErrors.phone}
                          helperText={profileErrors.phone || " "}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon sx={{ color: "text.secondary" }} />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <TextField
                          label="Date of Birth"
                          type="date"
                          value={profile.date_of_birth || ""}
                          onChange={(e) =>
                            setProfile((p) => ({ ...p, date_of_birth: e.target.value }))
                          }
                          fullWidth
                          disabled={!canEdit}
                          InputLabelProps={{ shrink: true }}
                          helperText=" "
                        />
                      </Box>
                    </Box>

                    <Box data-tour="cv-role-section">
                      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1.5 }}>
                        Role
                      </Typography>

                      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" } }}>
                        <TextField
                          label="Current / Proposed Position"
                          value={profile.current_job_title}
                          onChange={(e) => {
                            const val = e.target.value;
                            setProfile((p) => ({ ...p, current_job_title: val }));
                            if (profileErrors.current_job_title) {
                              setProfileErrors((prev) => {
                                const { current_job_title, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                          fullWidth
                          disabled={!canEdit}
                          required
                          error={!!profileErrors.current_job_title}
                          helperText={profileErrors.current_job_title || " "}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BusinessCenterIcon sx={{ color: "text.secondary" }} />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <TextField
                          label="Years of Experience"
                          value={profile.years_experience}
                          onChange={(e) => {
                            const val = e.target.value;
                            setProfile((p) => ({ ...p, years_experience: val }));
                            if (profileErrors.years_experience) {
                              setProfileErrors((prev) => {
                                const { years_experience, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                          fullWidth
                          disabled={!canEdit}
                          type="number"
                          required
                          error={!!profileErrors.years_experience}
                          helperText={profileErrors.years_experience || " "}
                          InputProps={{
                            inputProps: { min: 0, max: 50 },
                            endAdornment: (
                              <InputAdornment position="end">
                                <Typography variant="body2" color="text.secondary">
                                  years
                                </Typography>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1.5 }}>
                        Professional Summary
                      </Typography>

                      <TextField
                        label="About me"
                        value={profile.about_me}
                        onChange={(e) => {
                          const val = e.target.value;
                          setProfile((p) => ({ ...p, about_me: val }));
                          if (profileErrors.about_me) {
                            setProfileErrors((prev) => {
                              const { about_me, ...rest } = prev;
                              return rest;
                            });
                          }
                        }}
                        fullWidth
                        multiline
                        rows={5}
                        disabled={!canEdit}
                        placeholder="Write a short summary (skills, achievements, goals)..."
                        required
                        error={!!profileErrors.about_me}
                        helperText={profileErrors.about_me || "Recommended: 150-300 characters."}
                      />
                    </Box>
                  </Stack>
                </Stack>
              )}

              {/* Lists */}
              {tab >= 2 && tab <= 5 && (
                <Stack spacing={1.5}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 900 }}>
                        {tabItems[tab].label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mt: 0.5 }}>
                        {listData.length === 0
                          ? `Add your ${tabItems[tab].label.toLowerCase()} to strengthen your CV.`
                          : `${listData.length} item${listData.length !== 1 ? "s" : ""} added.`}
                      </Typography>

                      {(tab === 2 || tab === 3 || tab === 4 || tab === 5) && listData.length === 0 && (
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 900, color: "error.main", display: "block", mt: 0.5 }}
                        >
                          Required: At least 1 item before submitting.
                        </Typography>
                      )}
                    </Box>

                    <PrimaryButton
                      data-tour="cv-add-item"
                      startIcon={<AddIcon />}
                      onClick={() => openDialog(TAB_TYPES[tab], "add")}
                      disabled={!canEdit}
                      variant="contained"
                    >
                      Add
                    </PrimaryButton>
                  </Box>

                  {listData.length === 0 ? (
                    <Card
                      sx={{
                        p: { xs: 3, md: 5 },
                        textAlign: "center",
                        borderRadius: "14px",
                        border: `2px dashed ${theme.palette.divider}`,
                        bgcolor: "transparent",
                      }}
                    >
                      <Box sx={{ fontSize: 74, color: "grey.300", mb: 2 }}>
                        {tab === 2 ? (
                          <WorkIcon fontSize="inherit" />
                        ) : tab === 3 ? (
                          <SchoolIcon fontSize="inherit" />
                        ) : tab === 4 ? (
                          <LanguageIcon fontSize="inherit" />
                        ) : (
                          <EmojiEventsIcon fontSize="inherit" />
                        )}
                      </Box>

                      <Typography variant="h6" sx={{ fontWeight: 900, color: "text.secondary" }}>
                        No {tabItems[tab].label.toLowerCase()} yet
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 700, mt: 1, mb: 3, maxWidth: 520, mx: "auto" }}
                      >
                        Add items to make your profile complete and ready for HR review.
                      </Typography>

                      <PrimaryButton
                        startIcon={<AddIcon />}
                        onClick={() => openDialog(TAB_TYPES[tab], "add")}
                        disabled={!canEdit}
                        variant="contained"
                      >
                        Add First Item
                      </PrimaryButton>
                    </Card>
                  ) : (
                    <Box sx={{ width: "100%" }}>
                      <DataGrid
                        rows={listData}
                        columns={listColumns}
                        getRowId={(r) => r._id || r.id}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        disableColumnFilter
                        pageSizeOptions={[5, 10, 25]}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                        sx={{
                          height: 460,
                          borderRadius: "12px",
                          border: "1px solid",
                          borderColor: "divider",
                          "& .MuiDataGrid-columnHeaders": {
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                            borderBottom: `1px solid ${theme.palette.divider}`,
                          },
                          "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 900 },
                          "& .MuiDataGrid-cell": { borderBottom: `1px solid ${theme.palette.divider}` },
                          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": { outline: "none" },
                          "& .MuiDataGrid-row:hover": { bgcolor: alpha(theme.palette.primary.main, 0.02) },
                        }}
                      />
                    </Box>
                  )}
                </Stack>
              )}
            </Box>
          </PremiumCard>
        </Box>

        {/* FAB */}
        {!locked && canEdit && (
          <Zoom in={!busy}>
            <Fab
              color="primary"
              sx={{
                position: "fixed",
                bottom: 28,
                right: 28,
                boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
              }}
              onClick={saveAll}
              disabled={busy}
            >
              <SaveIcon />
            </Fab>
          </Zoom>
        )}

        {/* Busy overlay */}
        {busy && (
          <Fade in={busy}>
            <Box
              sx={{
                position: "fixed",
                inset: 0,
                bgcolor: "rgba(0,0,0,0.35)",
                display: "grid",
                placeItems: "center",
                zIndex: 9999,
                backdropFilter: "blur(2px)",
              }}
            >
              <PremiumCard sx={{ p: 4, minWidth: 320, textAlign: "center" }}>
                <Stack spacing={2.5} alignItems="center">
                  <CircularProgress size={52} thickness={4} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>
                      Saving...
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, mt: 0.5 }}>
                      Please wait a moment.
                    </Typography>
                  </Box>
                </Stack>
              </PremiumCard>
            </Box>
          </Fade>
        )}

        {/* Dialog */}
        <Dialog
          open={dlg.open}
          onClose={closeDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: "14px", boxShadow: "0 18px 36px rgba(0,0,0,0.14)" } }}
        >
          <DialogTitle
            sx={{
              fontWeight: 1000,
              fontSize: "1.25rem",
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            {dlg.mode === "add" ? "Add" : "Edit"}{" "}
            {dlg.type ? dlg.type.charAt(0).toUpperCase() + dlg.type.slice(1) : ""}
          </DialogTitle>

          <DialogContent dividers sx={{ p: 2.5 }}>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
              }}
            >
              {dialogFields.map((f) => (
                <TextField
                  key={f.key}
                  label={f.label}
                  value={form?.[f.key] ?? ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((p) => ({ ...p, [f.key]: val }));
                    if (dlgErrors[f.key]) {
                      setDlgErrors((prev) => {
                        const { [f.key]: _, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  fullWidth
                  required={!!f.required}
                  type={f.type === "date" ? "date" : "text"}
                  InputLabelProps={f.type === "date" ? { shrink: true } : undefined}
                  multiline={!!f.multiline}
                  rows={f.rows || 1}
                  disabled={
                    !canEdit ||
                    (dlg.type === "experience" && f.key === "end_date" && !!form?._is_present) ||
                    (dlg.type === "certificate" && f.key === "expiry_date" && !!form?._no_expiry)
                  }
                  placeholder={f.placeholder}
                  error={!!dlgErrors[f.key]}
                  helperText={
                    dlgErrors[f.key] ||
                    (dlg.type === "experience" && f.key === "end_date" && form?._is_present
                      ? "Current job (Present)"
                      : dlg.type === "certificate" && f.key === "expiry_date" && form?._no_expiry
                      ? "This certificate has no expiry date"
                      : " ")
                  }
                  sx={{
                    gridColumn: { xs: "auto", md: f.multiline ? "1 / -1" : "auto" },
                  }}
                />
              ))}

              {dlg.type === "experience" && (
                <Box sx={{ gridColumn: "1 / -1", mt: -1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!form?._is_present}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            _is_present: e.target.checked,
                            end_date: e.target.checked ? "" : p?.end_date || "",
                          }))
                        }
                        disabled={!canEdit}
                      />
                    }
                    label="Present (I currently work here)"
                  />
                </Box>
              )}

              {dlg.type === "certificate" && (
                <Box sx={{ gridColumn: "1 / -1", mt: -1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!form?._no_expiry}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            _no_expiry: e.target.checked,
                            expiry_date: e.target.checked ? "" : p?.expiry_date || "",
                          }))
                        }
                        disabled={!canEdit}
                      />
                    }
                    label="No expiry date"
                  />
                </Box>
              )}

              {dlg.type === "certificate" && (
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.75 }}>
                    Certificate attachment
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ flexWrap: "wrap", rowGap: 1 }}
                  >
                    <input
                      id="cv-cert-file-input"
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        uploadDialogCertificateFile(file);
                      }}
                    />
                    <label htmlFor="cv-cert-file-input">
                      <Button
                        component="span"
                        size="small"
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        disabled={!canEdit || certFileBusy === "dialog:upload"}
                        sx={{ fontWeight: 800 }}
                      >
                        {form?.file_path ? "Replace file" : "Upload file"}
                      </Button>
                    </label>

                    {form?.file_path ? (
                      <>
                        <Tooltip
                          title={form?.id ? "Open" : "Save the CV first to open this file"}
                        >
                          <span>
                            <Button
                              size="small"
                              startIcon={<OpenInNewIcon />}
                              disabled={!form?.id}
                              onClick={() => runCertificateFile(form, "open")}
                            >
                              Open
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={form?.id ? "Download" : "Save the CV first to download this file"}
                        >
                          <span>
                            <Button
                              size="small"
                              startIcon={<DownloadIcon />}
                              disabled={!form?.id}
                              onClick={() => runCertificateFile(form, "download")}
                            >
                              Download
                            </Button>
                          </span>
                        </Tooltip>
                        <Button
                          size="small"
                          color="error"
                          disabled={!canEdit}
                          onClick={() =>
                            setForm((p) => ({ ...p, file_path: "", file_name: "", file_url: "" }))
                          }
                        >
                          Remove
                        </Button>
                      </>
                    ) : null}
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    {form?.file_path ? <AttachFileIcon fontSize="small" color="action" /> : null}
                    <Typography variant="caption" sx={{ color: "text.secondary", wordBreak: "break-all" }}>
                      {form?.file_path
                        ? certificateFileName(form) || form.file_path
                        : "PDF or image (max 8 MB). Optional, but recommended."}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${theme.palette.divider}` }}>
            <SecondaryButton onClick={closeDialog}>Cancel</SecondaryButton>
            <PrimaryButton
              variant="contained"
              onClick={saveDialogLocal}
              startIcon={<SaveIcon />}
              disabled={!canEdit}
            >
              Save
            </PrimaryButton>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack({ open: false, msg: "" })}
          message={snack.msg}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          TransitionComponent={Grow}
          ContentProps={{
            sx: {
              bgcolor: "background.paper",
              color: "text.primary",
              borderRadius: "12px",
              boxShadow: "0 10px 26px rgba(0,0,0,0.18)",
              border: `1px solid ${theme.palette.divider}`,
              fontWeight: 900,
            },
          }}
        />
      </Container>
    </Box>
  );
}
